import { useEffect, useState } from "react";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
} from "@tanstack/react-table";
import { useToast } from "../hooks/use-toast";
import { handleHttpError } from "../utils/utils";
import Spinner from "./spinner.components";
import type { ICreator } from "../types/models/creator.models.types";
import type { IPagination } from "../types/models/pagination.models.types";

import {
  useGetPendingGstAdminHttp,
  IGetKycPendingListResponse,
} from "../hooks/http/admin/kyc/creators/get-pending-gst.admin.http";
import {
  useGetPendingPayoutAdminHttp,
  IGetPayoutPendingListResponse,
} from "../hooks/http/admin/kyc/creators/get-pending-payout.admin.http";
import { useSearchGstAdminHttp } from "../hooks/http/admin/kyc/creators/search-gst.admin.http";
import { useSearchPayoutAdminHttp } from "../hooks/http/admin/kyc/creators/search-payout.admin.http";
import { useApproveGstAdminHttp } from "../hooks/http/admin/kyc/creators/approve-gst.admin.http";
import { useApprovePayoutAdminHttp } from "../hooks/http/admin/kyc/creators/approve-payout.admin.http";

import {
  KycApproveButton,
  KycRejectButton,
} from "./action-buttons/kyc/kyc-action.components";
import { KycType } from "../types/models/kyc.types";
import useKycApprovalsStore, { KycRowData } from "../store/kyc-approvals.store";
import { IPayoutMethod } from "../hooks/http/admin/kyc/creators/approve-payout.admin.http";
import { DateTime } from "luxon";
import { DataTableComponents } from "./tables/table.components";

interface KycApprovalTableProps {
  type: KycType.GST | KycType.PAYOUT;
  searchQuery: string;
}

export function KycApprovalTable({ type, searchQuery }: KycApprovalTableProps) {
  const { toast } = useToast();
  const { data, setData, appendData, removeById } = useKycApprovalsStore();
  const [pageNo, setPageNo] = useState(1);
  const [paginationData, setPaginationData] = useState<IPagination>();

  const creatorHooks = {
    get: {
      [KycType.GST]: useGetPendingGstAdminHttp,
      [KycType.PAYOUT]: useGetPendingPayoutAdminHttp,
    },
    search: {
      [KycType.GST]: useSearchGstAdminHttp,
      [KycType.PAYOUT]: useSearchPayoutAdminHttp,
    },
    approve: {
      [KycType.GST]: useApproveGstAdminHttp,
      [KycType.PAYOUT]: useApprovePayoutAdminHttp,
    },
  };

  const { execute: fetchDataHttp, fetching } = creatorHooks.get[type]();
  const {
    execute: searchDataHttp,
    fetching: searchFetching,
    cancel: cancelSearchHttp,
  } = creatorHooks.search[type]();

  const loadData = async (
    page: number,
    refresh = false,
    searchTerm = searchQuery,
  ) => {
    const response = searchTerm
      ? await searchDataHttp({
          queryParams: { query: searchTerm, page },
          body: null,
        })
      : await fetchDataHttp({ queryParams: { page }, body: null });

    if (response.success) {
      let newData: KycRowData[] = [];

      if (type === KycType.PAYOUT) {
        newData = (response.data as IGetPayoutPendingListResponse)
          .payoutMethods as KycRowData[];
      } else {
        newData = (response.data as IGetKycPendingListResponse)
          .creators as KycRowData[];
      }

      if (refresh) {
        setData(newData);
      } else {
        appendData(newData);
      }
      setPaginationData(response.data.pagination);
    } else {
      handleHttpError(response, toast);
    }
  };

  useEffect(() => {
    setPageNo(1);
    cancelSearchHttp();
    setData([]); // Clear stale data when switching tabs
    loadData(1, true, searchQuery);
  }, [type, setData, searchQuery]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (pageNo > 1) {
      loadData(pageNo, false, searchQuery);
    }
  }, [pageNo]);

  const columns = (() => {
    if (type === KycType.PAYOUT) {
      const payoutCols: ColumnDef<KycRowData>[] = [
        {
          accessorKey: "name",
          header: "Creator",
          cell: ({ row }) => {
            const item = row.original;
            return (
              <div className="flex flex-col w-40 font-medium tracking-tight text-white">
                {item.user?.name || (item as IPayoutMethod).name}
                <p className="text-secondary text-xs">
                  {item.user?.email || "N/A"}
                </p>
              </div>
            );
          },
        },
        {
          accessorKey: "payoutMethodType",
          header: "Method",
          cell: ({ row }) => (
            <div className="font-medium capitalize text-white">
              {(row.original as IPayoutMethod).payoutMethodType}
            </div>
          ),
        },
        {
          header: "Account No",
          cell: ({ row }) => {
            const item = row.original as IPayoutMethod;
            return (
              <div className="font-medium text-white">
                {item.bankAccountDetails?.accountNumber ||
                  item.upiDetails?.vpaId ||
                  "-"}
              </div>
            );
          },
        },
        {
          header: "IFSC",
          cell: ({ row }) => {
            const item = row.original as IPayoutMethod;
            return (
              <div className="font-medium text-white">
                {item.bankAccountDetails?.ifscCode || "-"}
              </div>
            );
          },
        },
        {
          header: "Name",
          cell: ({ row }) => {
            const item = row.original as IPayoutMethod;
            return (
              <div className="font-medium text-white">{item.name || "-"}</div>
            );
          },
        },
        {
          header: "PAN Name",
          cell: ({ row }) => {
            const item = row.original as IPayoutMethod;
            return (
              <div className="font-medium text-white">
                {item.submittedName || "-"}
              </div>
            );
          },
        },
        {
          header: "Legal Name",
          cell: ({ row }) => {
            const item = row.original as IPayoutMethod;
            return (
              <div className="font-medium text-white">
                {item.gst?.legalName || "-"}
              </div>
            );
          },
        },
        {
          header: "Trade Name",
          cell: ({ row }) => {
            const item = row.original as IPayoutMethod;
            return (
              <div className="font-medium text-white">
                {item.gst?.tradeName || "-"}
              </div>
            );
          },
        },
      ];
      return payoutCols;
    } else {
      const userCols: ColumnDef<KycRowData>[] = [
        {
          accessorKey: "name",
          header: "Creator Name",
          cell: ({ row }) => (
            <div className="flex flex-col w-40 font-medium tracking-tight text-white">
              {row.original.name}
              <p className="text-secondary text-xs">
                {(row.original as ICreator).email}
              </p>
            </div>
          ),
        },
        {
          header: "GST Number",
          cell: ({ row }: { row: { original: KycRowData } }) => {
            const item = row.original as ICreator;
            return (
              <div className="flex flex-col gap-1 font-medium text-white">
                {item.gst?.gst || "-"}
                <div className="flex flex-col gap-2 text-secondary text-xs mt-1">
                  <div className="flex flex-col">
                    <span>Trade Name:</span>
                    <span>{item.gst?.tradeName || "-"}</span>
                  </div>
                  <div className="flex flex-col">
                    <span>Legal Name:</span>
                    <span>{item.gst?.legalName || "-"}</span>
                  </div>
                </div>
              </div>
            );
          },
        },
        {
          header: "Business Name",
          cell: ({ row }: { row: { original: KycRowData } }) => {
            const item = row.original as ICreator;
            return (
              <div className="font-medium text-white">
                {item.gst?.businessName || "-"}
              </div>
            );
          },
        },
        {
          id: "phoneNumber",
          header: "Phone",
          cell: ({ row }) => (
            <div className="font-medium">
              {(row.original as ICreator).phoneNumber || "-"}
            </div>
          ),
        },
        {
          id: "gst-timestamp",
          header: "Timestamp",
          cell: ({ row }) => {
            const item = row.original as ICreator;
            return (
              <div className="font-medium">
                {item.gst?.timestamp
                  ? DateTime.fromISO(
                      new Date(item.gst.timestamp).toISOString(),
                    ).toFormat("dd-MM-yyyy hh:mm a")
                  : "-"}
              </div>
            );
          },
        },
      ];
      return userCols;
    }
  })();

  const allColumns: ColumnDef<KycRowData>[] = [
    ...columns,
    {
      header: "",
      id: "actions",
      cell: ({ row }) => (
        <div className="flex gap-2 items-center justify-end">
          <KycApproveButton
            id={row.original.id}
            onSuccess={() => removeById(row.original.id)}
            hook={creatorHooks.approve[type]}
            paramName={type === KycType.PAYOUT ? "payoutMethodId" : "creatorId"}
          />
          <KycRejectButton id={row.original.id} type={type} />
        </div>
      ),
    },
  ];

  const table = useReactTable({
    data: data || [],
    columns: allColumns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: true,
  });

  return (
    <div className="w-full flex flex-col gap-4">
      {(fetching || searchFetching) && data.length === 0 ? (
        <div className="flex justify-center items-center h-64">
          <Spinner width={20} />
        </div>
      ) : (
        <>
          <DataTableComponents
            table={table as ReturnType<typeof useReactTable>}
            columns={allColumns}
            loading={fetching || searchFetching}
            paginationData={paginationData}
            setPageNo={setPageNo}
            tableType="kyc"
            
          />
        </>
      )}
    </div>
  );
}
